'use client'

import { useEffect } from 'react'
import CloseIcon from '@ancon/wildcat-ui/shared/icons/close.svg'
import Button from '@ancon/wildcat-ui/web/orderweb/Button'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import omit from 'lodash/omit'

import { fetchOutletDetails } from '../../store/outletThunks'
import Modal from '../../../app/components/modal/Modal'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import {
  outletDetailsIdASelector,
  outletDetailsSelector,
  outletIsOutletDetailsPendingSelector,
  outletIsVisibleOutletDetailsModalSelector,
  outletListItemSelector,
} from '../../store/outletSelector'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import { outletSetIsVisibleOutletDetailsModal } from '../../store/outletSlice'
import { outletsSelectedOutletListItemSelector } from '../../../outlets/store/outletsSelectors'
import { ModalHeaderProps } from '../../../app/components/modal/ModalHeader'
import { OutletDetailsURLFlag } from '../../constants'
import hasOutletDetailsURLFlag from '../../utils/hasOutletDetailsURLFlag'

import OutletDetailsMetadata from './OutletDetailsMetaData'
import styles from './OutletDetailsModal.module.scss'
import OutletDetailModalSkeleton from './OutletDetailsModalSkeleton'
import OutletDetailsOpeningHours from './OutletDetailsOpeningHours'
import OutletInformation from './OutletInformation'

function OutletDetailModalHeader({ onClose }: ModalHeaderProps) {
  return (
    <div className={styles.modalHeader}>
      <Button variant="secondary" onClick={onClose}>
        <CloseIcon />
      </Button>
    </div>
  )
}

type OutletDetailsModalProps = {
  isInOutletsPage?: boolean
}

export default function OutletDetailsModal({
  isInOutletsPage,
}: OutletDetailsModalProps) {
  const dispatch = useAppDispatch()
  const isVisible = useAppSelector(outletIsVisibleOutletDetailsModalSelector)
  const outletDetailsId = useAppSelector(outletDetailsIdASelector)
  const isOutletDetailsPending = useAppSelector(
    outletIsOutletDetailsPendingSelector,
  )
  const outletDetails = useAppSelector(outletDetailsSelector)
  // Outlet
  const outletOutletListItem = useAppSelector(outletListItemSelector)
  // Outlets
  const outletsOutletListItem = useAppSelector(
    outletsSelectedOutletListItemSelector,
  )

  // Current outlet
  const outlet = isInOutletsPage ? outletsOutletListItem : outletOutletListItem

  const searchParams = useSearchParams()

  const router = useRouter()

  function handleModalClose() {
    dispatch(outletSetIsVisibleOutletDetailsModal(false))

    const hasOutletDetailsURLSlug = hasOutletDetailsURLFlag(searchParams)

    if (hasOutletDetailsURLSlug) {
      // Remove the outlet details URL flag from the query
      const updatedQuery = omit(router.query, OutletDetailsURLFlag)

      router.replace(
        {
          pathname: router.pathname,
          query: updatedQuery,
        },
        undefined,
        {
          shallow: true,
        },
      )
    }
  }

  // Fetch outlet details if not exists
  useEffect(() => {
    if (
      !isInOutletsPage &&
      isVisible &&
      !isOutletDetailsPending &&
      outlet &&
      outlet.id !== outletDetailsId
    ) {
      dispatch(fetchOutletDetails({ outletId: outlet.id }))
    }
  }, [
    isVisible,
    outlet,
    outletDetailsId,
    dispatch,
    isOutletDetailsPending,
    isInOutletsPage,
  ])

  return (
    <Modal
      className={styles.modal}
      isOpen={isVisible}
      onClose={handleModalClose}
      mobileModalMode="bottom-sheet"
      CustomModalHeader={OutletDetailModalHeader}
    >
      <div className={styles.container}>
        {outletDetails && outlet ? (
          <>
            <div className={styles.topContainer}>
              {!isOutletDetailsPending && outletDetails && (
                <OutletDetailsMetadata outletDetails={outletDetails} />
              )}
            </div>
            <div className={styles.bottomContainer}>
              <OutletDetailsOpeningHours outletListItem={outlet} />
              <OutletInformation
                outletDetails={outletDetails}
                outletListItem={outlet}
              />
            </div>
          </>
        ) : (
          <OutletDetailModalSkeleton />
        )}
      </div>
    </Modal>
  )
}
